import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  StatusTag,
  describeAuthFormError,
  formErrorClassName,
  formInputClassName,
  secondaryTextClassName,
  useAuth,
  type AuthFormError,
} from '@monecolier/ui';
import {
  createChildStudentAccessLink,
  getChildStudentAccount,
  type StudentAccessLinkResponse,
  type StudentAccount,
} from '@monecolier/api-client';

/**
 * STU-02 — accès de l'enfant à son Espace Élève, côté PARENT (titulaire OU
 * co-parent PAR-04 accepté : le backend vérifie `requireParentOwnsStudent`).
 *
 * Ne s'affiche QUE si l'école a déjà créé le compte (création réservée à
 * l'école, jamais au parent). Le parent génère un lien (activation tant que
 * l'enfant n'a jamais choisi de mot de passe, récupération ensuite) : il le
 * reçoit aussi par email et peut le copier ici pour le transmettre à son
 * enfant. Chaque nouveau lien invalide le précédent.
 */
export function ChildStudentAccessPanel({ studentId }: { studentId: string }) {
  const { t, i18n } = useTranslation();
  const { accessToken, apiClient } = useAuth();
  const [account, setAccount] = useState<StudentAccount | null>(null);
  const [issued, setIssued] = useState<StudentAccessLinkResponse | null>(null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<AuthFormError | null>(null);

  useEffect(() => {
    if (!accessToken) return;
    let cancelled = false;
    getChildStudentAccount(apiClient, studentId, accessToken)
      .then((res) => {
        if (!cancelled) setAccount(res.account);
      })
      .catch(() => {
        // Best-effort : sans réponse, la section ne s'affiche simplement pas.
        if (!cancelled) setAccount(null);
      });
    return () => {
      cancelled = true;
    };
  }, [apiClient, accessToken, studentId]);

  if (!account) return null;

  async function handleGenerate() {
    setError(null);
    setCopied(false);
    setBusy(true);
    try {
      const res = await createChildStudentAccessLink(apiClient, studentId, accessToken!);
      setIssued(res);
      setAccount(res.account);
    } catch (err) {
      setError(describeAuthFormError(err));
    } finally {
      setBusy(false);
    }
  }

  async function handleCopy() {
    if (!issued) return;
    try {
      await navigator.clipboard.writeText(issued.link);
      setCopied(true);
    } catch {
      // Presse-papiers indisponible (contexte non sécurisé, WebView) : le lien
      // reste sélectionnable dans le champ ci-dessus.
      setCopied(false);
    }
  }

  const isActive = account.status === 'ACTIVE';
  const expiresAt = issued
    ? new Intl.DateTimeFormat(i18n.language, { dateStyle: 'long', timeStyle: 'short' }).format(new Date(issued.expiresAt))
    : null;

  return (
    <div className="flex flex-col gap-2 rounded-md border border-gray-200 p-3" data-testid="child-student-access">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm font-semibold text-gray-900">{t('childStudentAccess.title')}</p>
        <StatusTag tone={isActive ? 'green' : 'amber'}>
          {isActive ? t('childStudentAccess.statusActive') : t('childStudentAccess.statusPending')}
        </StatusTag>
      </div>
      <p className={secondaryTextClassName}>
        {t('childStudentAccess.username')}{' '}
        <strong data-testid="child-student-access-username">{account.username}</strong>
      </p>
      <p className={secondaryTextClassName}>
        {isActive ? t('childStudentAccess.helpActive') : t('childStudentAccess.helpPending')}
      </p>

      {issued ? (
        <div className="flex flex-col gap-2" data-testid="child-student-access-link-block">
          <label className="flex flex-col gap-1 text-sm font-medium text-text-secondary">
            {issued.kind === 'ACTIVATION' ? t('childStudentAccess.activationLink') : t('childStudentAccess.resetLink')}
            <input
              type="text"
              readOnly
              value={issued.link}
              onFocus={(event) => event.currentTarget.select()}
              data-testid="child-student-access-link"
              className={formInputClassName}
            />
          </label>
          <p className={secondaryTextClassName} role="status">
            {t('childStudentAccess.issued', { date: expiresAt })}
          </p>
        </div>
      ) : null}

      {error ? (
        <p role="alert" className={formErrorClassName(error.kind)} data-testid="child-student-access-error">
          {error.message}
        </p>
      ) : null}

      {/* Actions groupées sur une ligne, à la taille de leur contenu : une fois
          le lien affiché, "Copier" est l'action principale et "Générer un
          autre lien" passe au second plan. */}
      <div className="flex flex-wrap items-center gap-2">
        {issued ? (
          <Button type="button" size="small" onClick={handleCopy} data-testid="child-student-access-copy">
            {copied ? t('childStudentAccess.copied') : t('childStudentAccess.copy')}
          </Button>
        ) : null}
        <Button
          type="button"
          size="small"
          variant={issued ? 'secondary' : 'primary'}
          onClick={handleGenerate}
          disabled={busy}
          data-testid="child-student-access-generate"
        >
          {busy
            ? t('childStudentAccess.generating')
            : issued
              ? t('childStudentAccess.regenerate')
              : isActive
                ? t('childStudentAccess.generateReset')
                : t('childStudentAccess.generateActivation')}
        </Button>
      </div>
    </div>
  );
}
